import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import BehaviorFish from '@/assets/icons/behavior_fish.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import { PLACEHOLDER_IMAGE_SRC } from '../../helpers/placeholderImage';

import styles from './FilmstripCard.module.scss';

export interface FilmstripCardProps {
    key?: string;
    healing: number;
    active: number;
    skottelus: number;
    adult_female_lice: number;
    chilean_female_lice: number;
    laksvelScores: string[];
    movable: number;
    imageId: string;
    imagePath?: string;
    lazyLoadImage?: boolean;
    currentVisibleImageId: string | undefined;
    onClickHandler?: (event: MouseEvent<HTMLButtonElement>) => void;
    showWoundLabel?: boolean;
    showLiceLabel?: boolean;
    showLaksvelLabel?: boolean;
    showChileanLiceLabel?: boolean;
}

export default function FilmstripCard(props: FilmstripCardProps) {
    const {
        healing,
        active,
        skottelus,
        adult_female_lice,
        chilean_female_lice,
        laksvelScores = [],
        movable,
        imageId,
        imagePath,
        lazyLoadImage = true,
        currentVisibleImageId,
        onClickHandler = () => {},
        showWoundLabel = true,
        showLiceLabel = false,
        showLaksvelLabel = false,
        showChileanLiceLabel = false,
    } = props;

    const isMatchingVisibleImage = currentVisibleImageId === imageId;
    const { t } = useTranslation();
    // The lazy loader swaps `src` outside React, so the photo's own load event is the only
    // signal that the placeholder fish can go.
    const [loadedPath, setLoadedPath] = useState<string | null>(null);
    const showPlaceholder = !imagePath || loadedPath !== imagePath;

    return (
        <button
            type="button"
            name="fishHealthNavigationCard"
            value={imageId}
            data-iscurrent={isMatchingVisibleImage}
            aria-current={isMatchingVisibleImage ? 'true' : undefined}
            className={classNames(
                styles.fishHealthNavigationCard,
                isMatchingVisibleImage ? styles.active : ''
            )}
            onClick={onClickHandler}>
            <figure>
                <img
                    src={
                        lazyLoadImage ? PLACEHOLDER_IMAGE_SRC : (imagePath ?? PLACEHOLDER_IMAGE_SRC)
                    }
                    {...(lazyLoadImage && imagePath ? { 'data-src': imagePath } : {})}
                    alt={`${t('organisms.imageViewer.imageFromBioscope')} ${imageId}`}
                    onLoad={(event) => {
                        if (event.currentTarget.getAttribute('src') === imagePath) {
                            setLoadedPath(imagePath);
                        }
                    }}
                    onError={(event) => {
                        // A failed photo would draw the browser's broken-image icon and the alt
                        // text over the tile; fall back to the placeholder fish instead.
                        if (event.currentTarget.getAttribute('src') !== PLACEHOLDER_IMAGE_SRC) {
                            event.currentTarget.setAttribute('src', PLACEHOLDER_IMAGE_SRC);
                        }
                    }}
                />
                {showPlaceholder && (
                    <SvgIcon
                        icon={BehaviorFish}
                        size="inherit"
                        className={styles.placeholderFish}
                        svgProps={{ 'aria-hidden': true }}
                    />
                )}
            </figure>
            {showWoundLabel && (
                <ul>
                    <li data-type="active" data-hasvalue={active > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.active')}</span>
                        <span>{active}</span>
                    </li>
                    <li data-type="healing" data-hasvalue={healing > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.healing')}</span>
                        <span>{healing}</span>
                    </li>
                </ul>
            )}
            {showLiceLabel && (
                <ul>
                    <li data-type="adult_female_lice" data-hasvalue={adult_female_lice > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.adultFemaleLice')}</span>
                        <span>{adult_female_lice}</span>
                    </li>

                    <li data-type="movable" data-hasvalue={movable > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.movable')}</span>
                        <span>{movable}</span>
                    </li>
                    <li data-type="skottelus" data-hasvalue={skottelus > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.skottelus')}</span>
                        <span>{skottelus}</span>
                    </li>
                </ul>
            )}
            {showChileanLiceLabel && (
                <ul>
                    <li data-type="chilean_female_lice" data-hasvalue={chilean_female_lice > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.chileanFemaleLice')}</span>
                        <span>{chilean_female_lice}</span>
                    </li>
                    <li data-type="movable" data-hasvalue={movable > 0}>
                        <span data-type="color" />
                        <span>{t('organisms.imageViewer.movable')}</span>
                        <span>{movable}</span>
                    </li>
                </ul>
            )}
            {showLaksvelLabel &&
                laksvelScores.map((score) => {
                    return (
                        <ul key={`laksvel${score}`}>
                            <li data-type={score} data-hasvalue={score !== ''}>
                                <span data-type="color" />
                                <span>{t('organisms.filmstripCard.score')}</span>
                                <span>{score.split('_')[1]}</span>
                            </li>
                        </ul>
                    );
                })}
        </button>
    );
}
